import React, { useState, useEffect } from "react";
import {
  Paper,
  Group,
  Title,
  Text,
  ScrollArea,
  Stack,
  Card,
  SegmentedControl,
  Loader,
  Center,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  Badge,
  Checkbox,
} from "@mantine/core";
import {
  IconClipboardList,
  IconPlus,
  IconCheck,
  IconClock,
} from "@tabler/icons-react";
import { useAuth } from "../../../context/AuthContext";
import { getApiEndpoint } from "../../../config/config";
import TaskDetailModal from "./TaskDetailModal";
import { handleGenericError } from "../../../utils/errorHandler";

function TasksCard({ height = 300 }) {
  const { getVerifiedJWT } = useAuth();
  const [activeTab, setActiveTab] = useState("assigned");
  const [tasksData, setTasksData] = useState({
    createdTasks: [],
    assignedTasks: [],
    uncompletedCreatedCount: 0,
    uncompletedAssignedCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add Task Modal state
  const [modalOpened, setModalOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Task Detail Modal state
  const [taskDetailModalOpened, setTaskDetailModalOpened] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    description: "",
    dueDate: null,
    assignedUser: "",
    requiresImage: false,
    requiresAdminApproval: false,
  });

  const formatDueDate = dateString => {
    if (!dateString) return "No due date";

    try {
      const date = new Date(dateString);
      const options = { month: "short", day: "numeric" };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication required");
      }

      const response = await fetch(getApiEndpoint("/v1/custom-task/list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data || result;

      setTasksData({
        createdTasks: data.created_tasks || [],
        assignedTasks: data.assigned_tasks || [],
        uncompletedCreatedCount: data.uncompleted_created_count || 0,
        uncompletedAssignedCount: data.uncompleted_assigned_count || 0,
      });
    } catch (err) {
      setError(err.message);
      // Fallback to sample data on error
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication required");
      }

      const response = await fetch(getApiEndpoint("/v1/custom-task/users"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const result = await response.json();
      const userData = result.data || result;

      // Transform users data for Select component
      const userOptions = userData.map(user => ({
        value: String(user.id),
        label: `${user.first_name} ${user.last_name}`,
      }));

      setUsers(userOptions);
    } catch (err) {
      handleGenericError(error, "Unable to get task users");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setFormData({
      description: "",
      dueDate: null,
      assignedUser: "",
      requiresImage: false,
      requiresAdminApproval: false,
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication required");
      }

      const payload = {
        task_details: formData.description,
        due_date: formData.dueDate || null,
        assigned_user_season_id: formData.assignedUser || null,
        requires_image: formData.requiresImage,
        requires_admin_approval: formData.requiresAdminApproval,
      };

      const response = await fetch(getApiEndpoint("/v1/custom-task"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to create task: ${response.status}`);
      }

      // Refresh tasks after creation
      await fetchTasks();
      handleCloseModal();
    } catch (err) {
      handleGenericError(error, "Error creating task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTaskClick = task => {
    setSelectedTask(task);
    setTaskDetailModalOpened(true);
  };

  const handleTaskDetailModalClose = () => {
    setTaskDetailModalOpened(false);
    setSelectedTask(null);
  };

  const handleTaskUpdated = () => {
    fetchTasks();
    handleTaskDetailModalClose();
  };

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentTasks = () => {
    return activeTab === "created"
      ? tasksData.createdTasks
      : tasksData.assignedTasks;
  };

  const getUncompletedCreated = () => tasksData.uncompletedCreatedCount;
  const getUncompletedAssigned = () => tasksData.uncompletedAssignedCount;

  return (
    <>
      <Paper
        shadow="sm"
        p="lg"
        radius="lg"
        withBorder
        style={{
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
        }}
      >
        <Group align="center" mb="lg">
          <IconClipboardList
            size={28}
            style={{ color: "var(--mantine-color-green-6)" }}
          />
          <Title order={3}>Tasks</Title>
          <Button
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={handleOpenModal}
            style={{ marginLeft: "auto" }}
          >
            Add Task
          </Button>
        </Group>

        <Text size="sm" c="dimmed" mb="md">
          Your current tasks and deadlines
        </Text>

        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          data={[
            {
              label: `My Tasks (${getUncompletedAssigned()})`,
              value: "assigned",
            },
            {
              label: `Assigned Tasks (${getUncompletedCreated()})`,
              value: "created",
            },
          ]}
          mb="md"
          fullWidth
        />

        {loading ? (
          <Center h={height}>
            <Loader size="md" />
          </Center>
        ) : error ? (
          <Center h={height}>
            <Text c="red" size="sm">
              Error loading tasks.
            </Text>
          </Center>
        ) : (
          <ScrollArea h={height}>
            <Stack gap="sm">
              {getCurrentTasks().length === 0 ? (
                <Center h={200}>
                  <Text c="dimmed" size="sm">
                    No {activeTab} tasks found
                  </Text>
                </Center>
              ) : (
                getCurrentTasks().map(task => {
                  const isCompleted = task.completed_at;
                  const needsApproval =
                    task.requires_admin_approval &&
                    isCompleted &&
                    !task.admin_approved;
                  const isApproved = task.admin_approved;

                  return (
                    <Card
                      key={task.id}
                      shadow="xs"
                      padding="md"
                      radius="sm"
                      withBorder
                      style={{
                        cursor:
                          isCompleted && isApproved ? "default" : "pointer",
                        opacity: isCompleted && isApproved ? 0.8 : 1,
                        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                        border: "1px solid #e5e7eb",
                      }}
                      onClick={
                        isCompleted && isApproved
                          ? undefined
                          : () => handleTaskClick(task)
                      }
                    >
                      <Group align="center" justify="space-between" mb="xs">
                        <Group align="center" gap="sm">
                          {isCompleted && isApproved && (
                            <ActionIcon
                              variant="filled"
                              color="green"
                              size="sm"
                              style={{ cursor: "default" }}
                            >
                              <IconCheck size={12} />
                            </ActionIcon>
                          )}
                          {needsApproval && (
                            <Badge
                              size="sm"
                              color="yellow"
                              leftSection={<IconClock size={12} />}
                            >
                              Pending Approval
                            </Badge>
                          )}
                          <Text fw={500} size="sm">
                            {/* For assigned tasks, show the creator's name. For created tasks, show the assigned user's name */}
                            {activeTab === "assigned" && task.created_by_user
                              ? `${task.created_by_user.first_name} ${task.created_by_user.last_name}`
                              : activeTab === "assigned" && task.created_by_user
                                ? `${task.created_by_user.first_name} ${task.created_by_user.last_name}`
                                : `${task.assigned_user.first_name} ${task.assigned_user.last_name}`}
                          </Text>
                        </Group>
                        {task.due_date && (
                          <Text size="xs" c="gray">
                            Due: {formatDueDate(task.due_date)}
                          </Text>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {(() => {
                          // If this is a team event task, show only the event title (first line)
                          if (
                            task.task_details &&
                            task.task_details.includes("TEAM_EVENT:")
                          ) {
                            return task.task_details.split("\n")[0];
                          }
                          return task.task_details;
                        })()}
                      </Text>
                    </Card>
                  );
                })
              )}
            </Stack>
          </ScrollArea>
        )}
      </Paper>

      {/* Add Task Modal */}
      <Modal
        opened={modalOpened}
        onClose={handleCloseModal}
        title="Add New Task"
        size="md"
      >
        <Stack gap="md">
          <Textarea
            label="Task Description"
            placeholder="Enter task description"
            rows={3}
            value={formData.description}
            onChange={e =>
              setFormData({ ...formData, description: e.target.value })
            }
          />

          <TextInput
            label="Due Date (Optional)"
            placeholder="YYYY-MM-DD"
            type="date"
            value={formData.dueDate || ""}
            onChange={e =>
              setFormData({ ...formData, dueDate: e.target.value })
            }
          />

          <Select
            label="Assigned User"
            placeholder="Select user to assign"
            data={users}
            value={formData.assignedUser}
            onChange={value =>
              setFormData({ ...formData, assignedUser: value })
            }
            searchable
            clearable
            disabled={usersLoading}
          />

          <Stack gap="xs">
            <Checkbox
              label="Requires Image Upload"
              checked={formData.requiresImage}
              onChange={e =>
                setFormData({
                  ...formData,
                  requiresImage: e.currentTarget.checked,
                })
              }
              disabled={submitting}
            />
            <Checkbox
              label="Requires Admin Approval"
              checked={formData.requiresAdminApproval}
              onChange={e =>
                setFormData({
                  ...formData,
                  requiresAdminApproval: e.currentTarget.checked,
                })
              }
              disabled={submitting}
            />
          </Stack>

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              Create Task
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={taskDetailModalOpened}
        onClose={handleTaskDetailModalClose}
        task={selectedTask}
        taskType={activeTab}
        onTaskUpdated={handleTaskUpdated}
        users={users}
      />
    </>
  );
}

export default TasksCard;
